import type { HttpErrorResponse } from '@angular/common/http';

import type { EntityState, PickActions } from '@core/ngrx-legacy';
import { createEntityCollectionStore, getEntityCollectionActions } from '@core/ngrx-legacy';
import type { FetchGroupsPayload, GroupsQueryParams, GroupsState } from '@recruitee/views-types';

export const getStoreGroupId = (
  dataSourceId: string,
  groupingId: string,
  swimlaneGroupingId: string | null,
) => `${dataSourceId}:${groupingId}:${swimlaneGroupingId}`;
const idSelector = (entity: { id: string }) => entity.id;
const { actions, selectors, reducer, namespace } = createEntityCollectionStore<GroupsState>()(
  'ViewGroups',
  {
    idSelector,

    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);

      const fetch = (
        state: EntityState<GroupsState>,
        payload: {
          storeGroupId: string;
          dataSourceId: string;
          data: FetchGroupsPayload;
          queryParams: GroupsQueryParams;
        },
      ) =>
        adapter.markAsPending(state, {
          scope: null,
          entities: [payload.storeGroupId],
        });

      return {
        fetch,
        fetchSuccess: (state, payload: { data: GroupsState; concat: boolean }) => {
          const currentData = state.entityMap[payload.data.id];
          const data = payload.concat
            ? { ...payload.data, groups: [...(currentData?.groups ?? []), ...payload.data.groups] }
            : payload.data;
          return defaultActions.fetchOneSuccess(state, { data });
        },
        fetchFailed: (state, payload: { error: HttpErrorResponse; id: string }) =>
          defaultActions.fetchOneFailed(state, payload),

        fetchAll: (
          state: EntityState<GroupsState>,
          payload: {
            storeGroupId: string;
            dataSourceId: string;
            uuid: string;
            data: FetchGroupsPayload;
            // Search by name
            searchQuery: string;
          },
        ) =>
          adapter.markAsPending(state, {
            scope: null,
            entities: [payload.storeGroupId],
          }),
        fetchAllSuccess: (state, payload: { data: GroupsState; uuid: string }) =>
          defaultActions.fetchOneSuccess(state, { data: payload.data }),
        fetchAllFailed: defaultActions.fetchOneFailed,

        clear: (state, payload: { dataSourceId: string }) => {
          const recordsIds = Object.keys(state.entityMap).filter(
            id => id.split(':')[0] === payload.dataSourceId,
          );
          return adapter.removeMany(state, recordsIds);
        },
      };
    },
  },
);
export const ViewGroupsActions: PickActions<typeof actions, 'fetch' | 'fetchAll'> &
  Pick<typeof actions, 'clear'> = actions;
export {
  reducer as ViewGroupsReducer,
  namespace as ViewGroupsNamespace,
  selectors as ViewGroupsSelectors,
};
